/**
 * <header-component>
 *
 * Web Component that owns all header behavior:
 *  - keeps CSS variables in sync via ResizeObserver (no resize-listener + setTimeout)
 *  - detects sticky state via IntersectionObserver (no scroll polling)
 *  - rAF-throttled scroll handler for scroll-driven classes and transparent toggle
 *  - menu open/close on hover + focus, body scroll lock, aria-expanded management
 *  - responsive menu overlap logic (menu drops to its own row when links wrap)
 *  - dispatches `menu:open` / listens for `menu:close`
 *  - `data-underline-state` on `.header-link-desk` + `.header-link-underline` (account, bag)
 *    (`:hover` cannot do both; see `base.css` + `#onNavUnderlineEnterLi` / `#onNavUnderlineLeaveLi`)
 *
 * Loaded ONLY from `sections/main-header.liquid`. Does not live in global.js.
 *
 * External globals (accessed via window):
 *   - window.RevolutionSelectbox (global.js — selector widget for market/language)
 *   - @theme/body-scroll-lock
 *   - window.theme (theme.liquid inline — shared state for menu breakpoint)
 *
 * Public contract (do not break — CSS and other scripts depend on these):
 *   CSS vars set on documentElement:
 *     --header-height, --header-height-fixed, --submenu-position, --header-dynamic-height
 *   CSS classes:
 *     on this element: Header--Scrolled, Header--Transparent
 *     on parent:       header--Sticky (when sticky option enabled)
 *   Transparent header: `Header--Transparent` only while scrollY < 1px and no drawer/cart/mega (see `#updateScrollState`).
 *   Events:
 *     dispatches: menu:open
 *     listens:    menu:close (on body)
 *   ARIA contract (APG Disclosure pattern — https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/):
 *     trigger link  <a class="header-main-links" aria-expanded aria-controls="…">
 *     panel         <div id="desktop-menu-panel-…" data-desktop-menu="panel">
 *     `aria-expanded` lives on the link (the actual interactive control), not the <li>.
 *     `aria-haspopup` is intentionally NOT used — it would promise a `menu` keyboard
 *     model (arrow-key nav, Esc, Home/End) we don't implement.
 *
 * JS hooks (data-* — NOT aria-*; aria-submenu / aria-hasMenu / aria-singleMenu
 * were invalid ARIA names and ignored by assistive tech). Replaced with:
 *     [data-desktop-menu="trigger"] — top-level item with submenu (opens panel on hover/focus)
 *     [data-desktop-menu="single"]  — top-level item without submenu (closes after MENU_CLOSE_DELAY_MS)
 *     [data-desktop-menu="panel"]   — the submenu panel container
 */

import { RevolutionSelectbox } from '@theme/selectbox';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';

/** Desktop mega-menu — no scrollbar gap padding; content fills full width when locked. */
const desktopMenuScrollLockOptions = { reserveScrollBarGap: false };

/** Grace period before closing on hover so a diagonal path across Search (or any
 *  no-submenu link) does not collapse the panel before the pointer reaches it. */
const MENU_CLOSE_DELAY_MS = 200;

class HeaderComponent extends HTMLElement {
  #initialized = false;
  #options = { sticky: false, transparent: false };
  /** Active mega-menu panel passed to body-scroll-lock (the open Shop/Explore dropdown). */
  #desktopMenuScrollable = null;
  #desktopMenuWheelBound = false;
  #headerTransparent = false;
  /** Real hover primary input — avoids treating touch “sticky hover” as menu interaction. */
  #finePointerMql = window.matchMedia('(hover: hover) and (pointer: fine)');

  #resizeObserver = null;
  #stickyObserver = null;
  #scrollRafId = null;
  #menuOverlapRafId = null;
  #cartCountElement = null;

  #menuTriggers = [];
  #submenuTargets = [];
  #singleMenuTargets = [];
  #focusOutTargets = [];
  #desktopSublinkTriggers = [];
  /** @type {ReturnType<typeof setTimeout> | null} */
  #menuCloseTimer = null;

  /** Nav + utility <li> rows — pointer/focus underline L→R draw + L→R retract (see base.css). */
  #navUnderlineLis = [];
  /** @type {WeakMap<HTMLElement, ReturnType<typeof setTimeout>>} */
  #underlineRetractTimers = new WeakMap();

  #onResize = () => {
    if (this.#menuOverlapRafId !== null) return;
    this.#menuOverlapRafId = requestAnimationFrame(() => {
      this.#menuOverlapRafId = null;
      this.#menuOverlapHandler();
    });
  };

  #onSectionLoad = () => {
    globalThis.themeSyncLayoutHeights?.();
    this.#attachMenuListeners();
    this.#menuOverlapHandler();
  };

  #onCartUpdated = (event) => {
    const count = Number(event?.detail?.count);
    if (!Number.isFinite(count)) return;

    this.#updateCartCount(count);
  };

  #onScroll = () => {
    if (this.#scrollRafId !== null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  /**
   * When any drawer closes, or when a `popup:closed` fires (e.g. `#modal-country`
   * localization), re-evaluate the header's scroll-driven transparent/opaque state.
   * Body-scroll-lock and overlay popups suspend or alter scroll, so `Header--Transparent`
   * can drift out of sync with the real scroll position.
   * Double `rAF`: after popup/drawer close, `scrollY` can update one frame late.
   */
  #syncScrollDrivenHeaderState = () => {
    this.#updateScrollState();
    requestAnimationFrame(() => {
      this.#updateScrollState();
    });
  };

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#options = this.#parseOptions();
    this.#cartCountElement = this.querySelector('[data-cart-count]');

    this.#initSelectboxes();

    if (this.#options.sticky) {
      this.parentElement?.classList.add('header--Sticky');
    }

    this.#menuOverlapHandler();
    this.#setupHeightObserver();
    this.#setupStickyObserver();

    if (this.#options.transparent || this.#options.sticky) {
      document.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    document.body.addEventListener('menu:close', this.#closeMenu);
    document.body.addEventListener('search:state-change', this.#syncScrollDrivenHeaderState);
    document.body.addEventListener('drawer:opened', this.#syncScrollDrivenHeaderState);
    document.body.addEventListener('drawer:closed', this.#syncScrollDrivenHeaderState);
    document.body.addEventListener('popup:closed', this.#syncScrollDrivenHeaderState);
    document.addEventListener('cart:updated', this.#onCartUpdated);
    this.#attachMenuListeners();

    window.addEventListener('resize', this.#onResize, { passive: true });
    document.addEventListener('shopify:section:load', this.#onSectionLoad);

    this.#updateCartCount(window.theme?.cartCount ?? 0);
    this.#updateScrollState();
  }

  disconnectedCallback() {
    if (!this.#initialized) return;
    this.#initialized = false;

    this.#resizeObserver?.disconnect();
    this.#stickyObserver?.disconnect();

    if (this.#scrollRafId !== null) cancelAnimationFrame(this.#scrollRafId);
    if (this.#menuOverlapRafId !== null) cancelAnimationFrame(this.#menuOverlapRafId);

    document.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    document.body.removeEventListener('menu:close', this.#closeMenu);
    document.body.removeEventListener('search:state-change', this.#syncScrollDrivenHeaderState);
    document.body.removeEventListener('drawer:opened', this.#syncScrollDrivenHeaderState);
    document.body.removeEventListener('drawer:closed', this.#syncScrollDrivenHeaderState);
    document.body.removeEventListener('popup:closed', this.#syncScrollDrivenHeaderState);
    document.removeEventListener('cart:updated', this.#onCartUpdated);

    this.#detachMenuListeners();
    this.#cancelCloseMenu();
    this.#unlockPageScrollForDesktopMenu();

    this.parentElement?.classList.remove('header--Sticky');

    document.documentElement.style.removeProperty('--header-height');
    document.documentElement.style.removeProperty('--header-height-fixed');
    document.documentElement.style.removeProperty('--submenu-position');
    document.documentElement.style.removeProperty('--header-dynamic-height');
  }

  #updateCartCount(count) {
    if (!this.#cartCountElement) return;

    this.#cartCountElement.textContent = count;
  }

  #parseOptions() {
    try {
      return JSON.parse(this.getAttribute('data-settings')) ?? {};
    } catch {
      return {};
    }
  }

  #initSelectboxes() {
    this.querySelectorAll('.RevolutionSelectbox').forEach(
      (item) => new RevolutionSelectbox(item)
    );
  }

  #applyHeaderHeights() {
    const height = Math.round(this.getBoundingClientRect().height);
    if (height <= 0) return;

    const px = `${height}px`;
    const root = document.documentElement.style;
    root.setProperty('--header-height', px);
    root.setProperty('--submenu-position', px);
    root.setProperty('--header-dynamic-height', px);
  }

  #setupHeightObserver() {
    this.#applyHeaderHeights();
    this.#resizeObserver = new ResizeObserver(() => {
      this.#applyHeaderHeights();
    });
    this.#resizeObserver.observe(this);
  }

  #setupStickyObserver() {
    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
      },
      { threshold: 1 }
    );
    this.#stickyObserver.observe(this);
  }

  #attachMenuListeners() {
    this.#detachMenuListeners();

    this.#menuTriggers = Array.from(this.querySelectorAll('[data-desktop-menu="trigger"]'));
    this.#menuTriggers.forEach((el) => {
      el.addEventListener('mouseenter', this.#openMenu);
      el.addEventListener('focusin', this.#openMenu);
    });

    this.#submenuTargets = Array.from(this.querySelectorAll('[data-desktop-menu="panel"]'));
    this.#submenuTargets.forEach((el) => {
      el.addEventListener('mouseenter', this.#cancelCloseMenu);
      el.addEventListener('mouseleave', this.#scheduleCloseMenu);
    });

    this.#singleMenuTargets = Array.from(this.querySelectorAll('[data-desktop-menu="single"]'));
    this.#singleMenuTargets.forEach((el) => {
      el.addEventListener('mouseenter', this.#scheduleCloseMenu);
    });

    this.#focusOutTargets = Array.from(
      this.querySelectorAll('[data-desktop-menu="single"], .header-main-links')
    );
    this.#focusOutTargets.forEach((el) => {
      el.addEventListener('focusin', this.#closeMenu);
    });

    this.#desktopSublinkTriggers = Array.from(this.querySelectorAll('.desktop-menu__sublink'));
    this.#desktopSublinkTriggers.forEach((el) => {
      el.addEventListener('click', this.#onDesktopSublinkClick);
    });

    this.#navUnderlineLis = [
      ...this.querySelectorAll('.header-menu-items > .header-link-desk'),
      ...this.querySelectorAll('.header-link-list > .header-link-underline'),
    ];
    for (const li of this.#navUnderlineLis) {
      li.addEventListener('mouseenter', this.#onNavUnderlineEnterLi);
      li.addEventListener('mouseleave', this.#onNavUnderlineLeaveLi);
    }
  }

  #detachMenuListeners() {
    this.#cancelCloseMenu();
    this.#menuTriggers.forEach((el) => {
      el.removeEventListener('mouseenter', this.#openMenu);
      el.removeEventListener('focusin', this.#openMenu);
    });
    this.#submenuTargets.forEach((el) => {
      el.removeEventListener('mouseenter', this.#cancelCloseMenu);
      el.removeEventListener('mouseleave', this.#scheduleCloseMenu);
    });
    this.#singleMenuTargets.forEach((el) => {
      el.removeEventListener('mouseenter', this.#scheduleCloseMenu);
    });
    this.#focusOutTargets.forEach((el) => {
      el.removeEventListener('focusin', this.#closeMenu);
    });
    this.#desktopSublinkTriggers.forEach((el) => {
      el.removeEventListener('click', this.#onDesktopSublinkClick);
    });
    this.#menuTriggers = [];
    this.#submenuTargets = [];
    this.#singleMenuTargets = [];
    this.#focusOutTargets = [];
    this.#desktopSublinkTriggers = [];

    for (const li of this.#navUnderlineLis) {
      const pending = this.#underlineRetractTimers.get(li);
      if (pending !== undefined) {
        clearTimeout(pending);
        this.#underlineRetractTimers.delete(li);
      }
      li.removeEventListener('mouseenter', this.#onNavUnderlineEnterLi);
      li.removeEventListener('mouseleave', this.#onNavUnderlineLeaveLi);
      delete li.dataset.underlineState;
    }
    this.#navUnderlineLis = [];
  }

  #onNavUnderlineEnterLi = (event) => {
    const li = event.currentTarget;
    if (!(li instanceof HTMLElement)) return;
    const pending = this.#underlineRetractTimers.get(li);
    if (pending !== undefined) {
      clearTimeout(pending);
      this.#underlineRetractTimers.delete(li);
    }
    li.dataset.underlineState = 'visible';
  };

  #onNavUnderlineLeaveLi = (event) => {
    const li = event.currentTarget;
    if (!(li instanceof HTMLElement)) return;
    const next = event.relatedTarget;
    if (next instanceof Node && li.contains(next)) return;

    const pending = this.#underlineRetractTimers.get(li);
    if (pending !== undefined) {
      clearTimeout(pending);
      this.#underlineRetractTimers.delete(li);
    }

    li.dataset.underlineState = 'retracting';
    const timeoutId = setTimeout(() => {
      delete li.dataset.underlineState;
      this.#underlineRetractTimers.delete(li);
    }, 320);
    this.#underlineRetractTimers.set(li, timeoutId);
  };

  #cancelCloseMenu = () => {
    if (this.#menuCloseTimer === null) return;
    clearTimeout(this.#menuCloseTimer);
    this.#menuCloseTimer = null;
  };

  #scheduleCloseMenu = () => {
    this.#cancelCloseMenu();
    this.#menuCloseTimer = setTimeout(() => {
      this.#menuCloseTimer = null;
      this.#closeMenu();
    }, MENU_CLOSE_DELAY_MS);
  };

  #openMenu = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    this.#cancelCloseMenu();

    this.dispatchEvent(new CustomEvent('menu:open', { bubbles: true }));

    this.#updateMenuPosition();

    if (this.classList.contains('Header--Transparent')) {
      this.classList.remove('Header--Transparent');
      this.#headerTransparent = true;
    }

    target.querySelector('.header-main-links')?.setAttribute('aria-expanded', 'true');
    this.#activateFirstDesktopSublink(target);

    const siblings = Array.from(target.parentElement?.children ?? []).filter(
      (child) => child !== target
    );
    siblings.forEach((item) => {
      item.querySelector('.header-main-links[aria-expanded="true"]')
        ?.setAttribute('aria-expanded', 'false');
    });

    this.#lockPageScrollForDesktopMenu(target);
  };

  #closeMenu = () => {
    this.#cancelCloseMenu();
    this.querySelectorAll('.header-main-links[aria-expanded="true"]').forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('.desktop-menu__multiple-items.is-active').forEach((item) => {
      item.classList.remove('is-active');
    });
    this.querySelectorAll('.desktop-menu__sublink.is-active').forEach((item) => {
      item.classList.remove('is-active');
    });
    this.querySelectorAll('.desktop-menu__sublink[aria-expanded="true"]').forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
    });

    this.#updateScrollState();

    this.#unlockPageScrollForDesktopMenu();
  };

  #getOpenDesktopMenuPanel() {
    const openLink = this.querySelector('.header-main-links[aria-expanded="true"]');
    const panel = openLink?.nextElementSibling;
    return panel instanceof HTMLElement && panel.matches('[data-desktop-menu="panel"]')
      ? panel
      : null;
  }

  #onDesktopMenuWheel = (event) => {
    const panel = this.#getOpenDesktopMenuPanel();
    if (!panel) return;
    if (panel.contains(event.target)) return;
    event.preventDefault();
  };

  #lockPageScrollForDesktopMenu(menuTriggerLi) {
    if (!this.#finePointerMql.matches) return;

    const panel = menuTriggerLi.querySelector('[data-desktop-menu="panel"]');
    const scrollable =
      panel instanceof HTMLElement && panel.hasAttribute('data-scrollable')
        ? panel
        : panel?.querySelector('[data-scrollable]');

    const isFirstLock = !document.body.classList.contains('desktop-menu-scroll-lock');

    if (isFirstLock) {
      document.body.classList.add('scrollLock', 'desktop-menu-scroll-lock');
    }

    if (scrollable instanceof HTMLElement && scrollable !== this.#desktopMenuScrollable) {
      if (this.#desktopMenuScrollable) {
        enableBodyScroll(this.#desktopMenuScrollable);
      }
      this.#desktopMenuScrollable = scrollable;
    }

    if (this.#desktopMenuScrollable) {
      disableBodyScroll(this.#desktopMenuScrollable, desktopMenuScrollLockOptions);
    }

    if (!this.#desktopMenuWheelBound) {
      document.addEventListener('wheel', this.#onDesktopMenuWheel, {
        passive: false,
        capture: true,
      });
      this.#desktopMenuWheelBound = true;
    }
  }

  #unlockPageScrollForDesktopMenu() {
    if (this.#desktopMenuScrollable) {
      enableBodyScroll(this.#desktopMenuScrollable);
      this.#desktopMenuScrollable = null;
    }

    if (!this.#getOpenDesktopMenuPanel()) {
      document.body.classList.remove('scrollLock', 'desktop-menu-scroll-lock');

      if (this.#desktopMenuWheelBound) {
        document.removeEventListener('wheel', this.#onDesktopMenuWheel, { capture: true });
        this.#desktopMenuWheelBound = false;
      }
    }
  }

  #onDesktopSublinkClick = (event) => {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;

    const key = target.dataset.submenuKey;
    if (!key) return;
    const scope = target.closest('[data-desktop-menu="panel"]');
    if (!(scope instanceof HTMLElement)) return;
    const contentPanel = scope.querySelector(`.desktop-menu__multiple-items[data-submenu-key="${key}"]`);
    if (!(contentPanel instanceof HTMLElement)) return;

    const alreadyActive = contentPanel.classList.contains('is-active');
    if (!alreadyActive) {
      event.preventDefault();
    }
    this.#setActiveDesktopSubmenu(scope, key);
  };

  #activateFirstDesktopSublink(menuTrigger) {
    const scope = menuTrigger.querySelector('[data-desktop-menu="panel"]');
    if (!(scope instanceof HTMLElement)) return;
    if (scope.querySelector('.desktop-menu__multiple-items.is-active')) return;

    const firstSublink = scope.querySelector('.desktop-menu__sublink[data-submenu-key]');
    const key = firstSublink?.dataset.submenuKey;
    if (!key) return;
    this.#setActiveDesktopSubmenu(scope, key);
  }

  #setActiveDesktopSubmenu(scope, key) {
    scope.querySelectorAll('.desktop-menu__multiple-items.is-active').forEach((el) => {
      el.classList.remove('is-active');
    });
    scope.querySelectorAll('.desktop-menu__sublink.is-active').forEach((el) => {
      el.classList.remove('is-active');
    });
    scope.querySelectorAll('.desktop-menu__sublink[aria-expanded="true"]').forEach((el) => {
      el.setAttribute('aria-expanded', 'false');
    });

    const nextPanel = scope.querySelector(`.desktop-menu__multiple-items[data-submenu-key="${key}"]`);
    const nextSublink = scope.querySelector(`.desktop-menu__sublink[data-submenu-key="${key}"]`);

    if (nextPanel instanceof HTMLElement) {
      nextPanel.classList.add('is-active');
    }
    if (nextSublink instanceof HTMLElement) {
      nextSublink.classList.add('is-active');
      nextSublink.setAttribute('aria-expanded', 'true');
      const shopAll = scope.querySelector('[data-desktop-menu-shop-all] a[href]');
      const href = nextSublink.getAttribute('href');
      if (shopAll instanceof HTMLAnchorElement && href) {
        shopAll.setAttribute('href', href);
      }
    }
  }

  #updateMenuPosition() {
    document.documentElement.style.setProperty(
      '--submenu-position',
      `${this.offsetHeight}px`
    );
  }

  #updateScrollState() {
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    /** Transparent header only flush with scroll top; first px of scroll → solid bar. */
    const atScrollTop = scrollY < 1;

    if (!atScrollTop) {
      this.classList.add('Header--Scrolled');
    } else {
      this.classList.remove('Header--Scrolled');
    }

    if (this.#options.transparent) {
      const drawerOpen = document.querySelector(
        'drawer-component[aria-expanded="true"], cart-drawer[aria-expanded="true"]'
      );
      const desktopMenuOpen = !!this.querySelector('.header-main-links[aria-expanded="true"]');
      const finePointerHover = this.#finePointerMql.matches;
      const desktopPanelHovered =
        finePointerHover && !!this.querySelector('[data-desktop-menu="panel"]:hover');

      const searchOpen = document.body.classList.contains('search-is-open');
      const holdSolid =
        drawerOpen ||
        searchOpen ||
        !atScrollTop ||
        desktopMenuOpen ||
        desktopPanelHovered;

      if (holdSolid) {
        this.classList.remove('Header--Transparent');
      } else {
        this.classList.add('Header--Transparent');
      }

      globalThis.themeUpdateFwmTextOffset?.();
    }

    const rect = this.getBoundingClientRect();
    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
    );
    document.documentElement.style.setProperty(
      '--header-dynamic-height',
      `${Math.round(visibleHeight)}px`
    );
  }

  #menuOverlapHandler() {
    const menuItems = this.querySelector('.header-menu-items');
    const menuWrapper = this.querySelector('.header--Wrapper');
    if (!menuItems || !menuWrapper) return;

    const currentClientWidth = Math.max(
      document.body.scrollWidth,
      document.documentElement.scrollWidth,
      document.body.offsetWidth,
      document.documentElement.offsetWidth,
      document.documentElement.clientWidth
    );

    const heights = [];
    this.querySelectorAll('.header-main-links').forEach((item) =>
      heights.push(item.offsetHeight)
    );

    const maxHeight = heights.length ? Math.max.apply(null, heights) : 0;
    const minHeight = heights.length ? Math.min.apply(null, heights) : 0;
    const overlapping = maxHeight > minHeight;

    window.theme = window.theme || {};

    if (overlapping && (!window.theme.menuBreakpoint || window.theme.menuBreakpoint === 0)) {
      window.theme.menuBreakpoint = currentClientWidth;
    }

    if (currentClientWidth > (window.theme.menuBreakpoint || 0)) {
      menuItems.classList.remove('mt-m', 'mainMenu--Overlapping');
      menuWrapper.querySelector('.header-main-menu-items')?.prepend(menuItems);
      window.theme.menuBreakpoint = 0;
    } else if (overlapping) {
      menuItems.classList.add('mt-m', 'mainMenu--Overlapping');
      menuItems.closest('.header')?.appendChild(menuItems);
    }

  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
